import { Injectable } from '@angular/core';
import { GridInput } from '@it2go/data-grid';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import { SocialProjectObject } from '@libs/types/src/services/object-type/service/social-project.object';
import { SocialProjectCreateInput } from '@libs/types/src/services/input-type/services/project.create-input';
import { SocialProjectUpdateInput } from '@libs/types/src/services/input-type/services/project.update-input';
import { SocialProjectMonitoringReportInput } from '@libs/types/src/services/input-type/services/social-project-monitoring-report.input';
import { SocialProjectMonitoringReportObject } from '@libs/types/src/services/object-type/service/social-project-monitoring-report.object';
import { SocialProjectClientsWorkReportInput } from '@libs/types/src/services/input-type/services/social-project-clients-work-report.input';
import { SocialProjectClientsWorkReportObject } from '@libs/types/src/services/object-type/service/social-project-clients-work-report.object';
import { OkObject } from '@it2go/types';
import { filterFrag } from './types/filter.types';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';

const counterValueFrag = `start increase end`;

const socialProjectMonitoringReportFrag = `
    meta {
        projectType
        i18nPrefix
        serviceTypeName
        showBirthdate
        supportsOnlyDecreeActivities
        regionLabelKey
        primaryIndicatorKeys
        supplementaryKeys
    }
    staffGroups {
        titleKey
        members {
            userId
            name
            surname
            positionCategory
            positionName
            employmentDateFrom
            employmentDateTo
            education
            fieldOfStudy
        }
    }
    primaryIndicators {
        newUsers { ${counterValueFrag} }
        newUsersByGender {
            male { ${counterValueFrag} }
            female { ${counterValueFrag} }
            total { ${counterValueFrag} }
        }
        bedDays { ${counterValueFrag} }
        personDays { ${counterValueFrag} }
        userMonths { ${counterValueFrag} }
        contacts { ${counterValueFrag} }
        interventions { ${counterValueFrag} }
        groupActivities { ${counterValueFrag} }
    }
    anonymousUsers {
        name
        birthdate
        dateAdded
        contacts { ${counterValueFrag} }
        contactsAdult { ${counterValueFrag} }
        interventions { ${counterValueFrag} }
        interventionsAdult { ${counterValueFrag} }
    }
    supplementary {
        groupActivities { ${counterValueFrag} }
        groupActivityParticipants { ${counterValueFrag} }
        groupActivityParticipantsChildren { ${counterValueFrag} }
        groupActivityParticipantsAdults { ${counterValueFrag} }
        contacts { ${counterValueFrag} }
        contactsAdult { ${counterValueFrag} }
        interventions { ${counterValueFrag} }
        interventionsAdult { ${counterValueFrag} }
        byAge { label value { ${counterValueFrag} } }
        byResidence { label value { ${counterValueFrag} } }
        userMonthsBreakdown { month count }
    }
    monitoringSheetUsers {
        name
        birthdate
        registrationDate
        directWorkHours { start increase end }
        contacts { ${counterValueFrag} }
        interventions { ${counterValueFrag} }
    }
`;

const clientWorkActivityMetricsFrag = `
    count
    durationMinutes
    units
    priceBeforeDiscount
`;

const socialProjectClientsWorkReportFrag = `
    clientGroups {
        folderId
        clientName
        birthYear
        permanentResidence
        offers {
            code
            name
            group { ${clientWorkActivityMetricsFrag} }
            individual { ${clientWorkActivityMetricsFrag} }
        }
    }
`;

@Injectable({ providedIn: 'root' })
export class ServiceSocialProjectGqlService extends GqlServiceV2 {
    public getSocialProjects(input: GridInput | null, frag: string) {
        return this.query(
            `
                query($input: GridInput) {
                    socialProject {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSocialProject(input: IdInput, frag: string) {
        return this.query(
            `
                query($input: IdInput!) {
                    socialProject {
                        get(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createSocialProject(input: SocialProjectCreateInput) {
        return this.mutate<SocialProjectObject>(
            `
            mutation($input: SocialProjectCreateInput!) {
                socialProject {
                    create(input: $input) {
                        id
                    }
                }
            }
            `,
            { input },
        );
    }

    public updateSocialProject(input: SocialProjectUpdateInput) {
        return this.mutate<SocialProjectObject>(
            `
            mutation($input: SocialProjectUpdateInput!) {
                socialProject {
                    update(input: $input) {
                        id
                    }
                }
            }
            `,
            { input },
        );
    }

    public deleteSocialProject(input: IdInput) {
        return this.mutate<OkObject>(
            `
            mutation($input: IdInput!) {
                socialProject {
                    delete(input: $input) {
                        ok
                    }
                }
            }
            `,
            { input },
        );
    }

    public getSocialProjectMonitoringReport(
        input: SocialProjectMonitoringReportInput,
    ) {
        return this.query<SocialProjectMonitoringReportObject>(
            `
                query($input: SocialProjectMonitoringReportInput!) {
                    projectReport {
                        getData(input: $input) {
                            ${socialProjectMonitoringReportFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSocialProjectClientsWorkReport(
        input: SocialProjectClientsWorkReportInput,
    ) {
        return this.query<SocialProjectClientsWorkReportObject>(
            `
                query($input: SocialProjectClientsWorkReportInput!) {
                    projectReport {
                        getClientsWorkData(input: $input) {
                            ${socialProjectClientsWorkReportFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }
}
